import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toUserMessage } from "../lib/errors";
import { ReportRunListItem } from "../lib/report";
import { reportClient } from "../lib/reportClient";

const labels: Record<string, string> = {
  planning: "計画作成中",
  plan_ready: "開始待ち",
  queued: "準備中",
  running: "作成中",
  succeeded: "完了",
  partial: "一部完了",
  failed: "失敗",
  cancelled: "キャンセル",
};

// backendのReportRunQueryService::splitFilter()はカンマ区切りのstatusをwhereInで
// 複数一致させるため、「作成中」タブは進行中の複数statusを1つのfilter値としてまとめて渡す。
const STATUS_FILTERS: { key: string; label: string; value?: string }[] = [
  { key: "all", label: "すべて" },
  { key: "in_progress", label: "作成中", value: "planning,plan_ready,queued,running" },
  { key: "succeeded", label: "完了", value: "succeeded" },
  { key: "partial", label: "一部完了", value: "partial" },
  { key: "failed", label: "失敗", value: "failed" },
];

const REPORT_TYPE_FILTERS: { key: string; label: string; value?: string }[] = [
  { key: "all", label: "すべて" },
  { key: "business", label: "ビジネス", value: "business" },
  { key: "general", label: "一般", value: "general" },
];

function FilterTabs({
  groupLabel,
  options,
  activeKey,
  onSelect,
}: {
  groupLabel: string;
  options: { key: string; label: string; value?: string }[];
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div role="group" aria-label={groupLabel} className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => onSelect(option.key)}
          aria-pressed={activeKey === option.key}
          className={
            activeKey === option.key
              ? "rounded-full bg-[#7C5A4E] px-4 py-1.5 text-xs font-bold text-white"
              : "rounded-full border border-[#EADBC8] bg-white px-4 py-1.5 text-xs font-bold text-[#8A746A] hover:border-[#A67C52]"
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default function ReportListPage() {
  const [items, setItems] = useState<ReportRunListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilterKey, setStatusFilterKey] = useState("all");
  const [reportTypeFilterKey, setReportTypeFilterKey] = useState("all");

  const [adding, setAdding] = useState<Record<string, boolean>>({});
  const [knowledgeErrors, setKnowledgeErrors] = useState<Record<string, string | null>>({});
  const pendingRuns = useRef(new Set<string>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const addKnowledge = async (runId: string) => {
    if (pendingRuns.current.has(runId)) return;
    pendingRuns.current.add(runId);
    setAdding(current => ({ ...current, [runId]: true }));
    setKnowledgeErrors(current => ({ ...current, [runId]: null }));
    try {
      const knowledge = await api.createKnowledgeItemFromDeepReport(runId);
      if (!mounted.current) return;
      setItems(current => current.map(item => item.runId === runId ? { ...item, knowledgeItemId: knowledge.id } : item));
    } catch (reason: unknown) {
      if (mounted.current) setKnowledgeErrors(current => ({ ...current, [runId]: toUserMessage(reason) }));
    } finally {
      pendingRuns.current.delete(runId);
      if (mounted.current) setAdding(current => ({ ...current, [runId]: false }));
    }
  };

  const statusValue = STATUS_FILTERS.find((option) => option.key === statusFilterKey)?.value;
  const reportTypeValue = REPORT_TYPE_FILTERS.find((option) => option.key === reportTypeFilterKey)?.value;

  // filterタブを連打すると複数のlistRuns呼び出しが並行して飛び、後勝ちだとタブの選択状態と
  // 表示itemsがずれる。useReportPolling.tsのcancelledガードと同じ考え方で、最新のload呼び出し
  // だけがsetItems/setCursorを反映するよう世代カウンタで古い応答を無視する。
  const loadGenerationRef = useRef(0);

  const load = async (next?: string) => {
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    try {
      const result = await reportClient.listRuns({
        cursor: next,
        limit: 20,
        status: statusValue,
        reportType: reportTypeValue,
      });
      if (generation !== loadGenerationRef.current) return;
      setItems((current) => (next ? [...current, ...result.items] : result.items));
      setCursor(result.nextCursor);
      setError(null);
    } catch (reason) {
      if (generation !== loadGenerationRef.current) return;
      setError(toUserMessage(reason));
    } finally {
      if (generation === loadGenerationRef.current) setLoading(false);
    }
  };

  // filterはcursorのfingerprintに含まれるため(report-redesign-api.md「一覧」)、filterを
  // 変えたら必ずcursor無しから読み直す。cursorは応答到着まで同期的にnullへ戻し、応答が返る前に
  // 「さらに読み込む」が押されて旧filterのcursorを新filterへ渡してしまう(400)のを防ぐ。
  useEffect(() => {
    setCursor(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilterKey, reportTypeFilterKey]);

  return (
    <main className="min-h-screen bg-[#FFF9F5] px-4 py-8 text-[#4A3E39]">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-black text-[#7C5A4E]">深掘りレポート</h1>
          <Link
            to="/request/deep-report"
            className="rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-bold text-white"
          >
            新規作成
          </Link>
        </div>

        <div className="mt-5 grid gap-2">
          <FilterTabs
            groupLabel="状態で絞り込む"
            options={STATUS_FILTERS}
            activeKey={statusFilterKey}
            onSelect={setStatusFilterKey}
          />
          <FilterTabs
            groupLabel="種類で絞り込む"
            options={REPORT_TYPE_FILTERS}
            activeKey={reportTypeFilterKey}
            onSelect={setReportTypeFilterKey}
          />
        </div>

        {error && (
          <p role="alert" className="mt-5 rounded-xl bg-[#FFF0EB] p-3 text-[#B4533A]">
            {error}
          </p>
        )}

        <section className="mt-6 grid gap-3">
          {items.map((item) => (
            <article
              key={item.runId}
              className="rounded-2xl border border-[#EADBC8] bg-white p-5 shadow-sm hover:border-[#A67C52]"
            >
              <div className="flex justify-between gap-4">
                <h2 className="font-black">{item.theme || "名称未設定"}</h2>
                <span className="shrink-0 text-xs font-bold text-[#A67C52]">
                  {labels[item.status] ?? item.status}
                </span>
              </div>
              <p className="mt-2 text-xs text-[#8A746A]">
                {item.createdAt ? new Date(item.createdAt).toLocaleString("ja-JP") : ""}
              </p>
              {item.summary && <p className="mt-2 line-clamp-2 text-sm">{item.summary}</p>}
              <Link to={`/reports/${encodeURIComponent(item.runId)}`} className="mt-3 inline-flex rounded-lg border border-[#DCC7BA] px-3 py-2 text-sm font-bold text-[#7C5A4E] hover:bg-[#F5EEE8]">詳細を見る</Link>
              {(item.status === "succeeded" || item.status === "partial") && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {item.knowledgeItemId ? (
                    <>
                      <span role="status" className="text-sm font-bold">✓ AIで利用できます</span>
                      <Link to={`/request/knowledge/${encodeURIComponent(item.knowledgeItemId)}`} state={{ returnTo: "/reports" }} className="text-sm font-bold text-[#7C5A4E] underline">ナレッジで見る</Link>
                    </>
                  ) : (
                    <button type="button" onClick={() => void addKnowledge(item.runId)} disabled={adding[item.runId]}
                      className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-bold disabled:opacity-40">
                      {adding[item.runId] ? "追加中..." : "AIで使えるようにする"}
                    </button>
                  )}
                  {knowledgeErrors[item.runId] && <p role="alert" className="w-full text-sm text-[#B4533A]">{knowledgeErrors[item.runId]}</p>}
                </div>
              )}
            </article>
          ))}
          {!loading && items.length === 0 && (
            <p className="rounded-2xl border border-dashed border-[#EADBC8] p-12 text-center">
              まだレポートはありません。
            </p>
          )}
        </section>

        {loading && <p className="mt-5 text-center">読み込み中...</p>}
        {cursor && (
          <button
            onClick={() => void load(cursor)}
            disabled={loading}
            className="mt-5 w-full rounded-full border border-[#A67C52] py-3 font-bold text-[#7C5A4E]"
          >
            さらに読み込む
          </button>
        )}
      </div>
    </main>
  );
}
